import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { AppDispatch, RootState } from 'store'
import {
  applyCoverageCover,
  applyCoverageReassign,
  clearCoverageCell,
  confirmCoverageSlot,
  getCoverCandidates,
  getCoverageCell,
  getCoverageMatrix,
  getReassignPreview,
  revokeCoverageConfirm,
  sendCoverageReminder,
} from 'features/coverage/coverageSlice'
import { fetchCoverageRegistries } from './coverageRegistryFetch'
import {
  ICoverageMatrixCellResponse,
  ICoverageMatrixRowResponse,
  ICoverageShiftResponse,
  IReassignSourceImpactResponse,
} from 'types/api/coverageApi.interface'
import { ISelectValue } from 'constants/globalTypes'
import { NavBar } from 'components/NavBar/NavBar'
import { Button } from 'components/ui/Button/Button'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { HonestSelectInput } from 'components/HonestSelect/HonestSelectInput'
import CalendarInput from 'components/CalendarInput/CalendarInput'
import { CloseIcon } from 'assets/icons/CloseIcon'
import { JustSpinner } from 'components/Loading/Loading'
import { PageWrapper, Flex } from 'styling/GlobalStyles'
import { Head1, OtherText, TitleText } from 'styling/Typography'
import { tokens } from 'styling/tokens'
import {
  coveragePhaseKey,
  coverageStatusTone,
  coverageTone,
  shiftWindowFrom,
  shiftWindowTo,
} from 'utils/coverageStatus'
import { coverageSitesFromTree } from './coverageRegistryHelpers'
import {
  HeaderControls,
  HistoricalBanner,
  StripWrap,
  LabelsCol,
  LaneLabel,
  RulerSpacer,
  TracksCol,
  LaneTrack,
  StripBar,
  RulerTrack,
  StripTickLabel,
  NowLine,
  MatrixScroll,
  MatrixTable,
  MatrixHeadCell,
  DeptCell,
  DeptCellName,
  DeptCellSub,
  Cell,
  EmptyCellBox,
  CellTop,
  CellNumbers,
  CellDot,
  CritBadge,
  CellSub,
  LegendRow,
  LegendSwatch,
  LegendDashed,
  Scrim,
  Panel,
  PanelHeader,
  PanelBody,
  PanelClose,
  SectionLabel,
  BreakdownRow,
  BreakdownLabel,
  BreakdownValue,
  PersonRow,
  PersonName,
  PersonRole,
  VacantRow,
  GapCallout,
  PanelActions,
  EmptyState,
} from './CoverageMatrix.styled'

const pct = (min: number) => (min / 1440) * 100

const STATE_PILL: { [state: number]: { label: string; tone: 'success' | 'warning' | 'danger' | 'info' | 'neutral' } } = {
  1: { label: 'no response', tone: 'warning' },
  2: { label: 'confirmed', tone: 'success' },
  3: { label: 'present', tone: 'success' },
  4: { label: 'late', tone: 'warning' },
  5: { label: 'break', tone: 'info' },
  6: { label: 'absent', tone: 'danger' },
  7: { label: 'unavailable', tone: 'danger' },
}

const rowLabel = (r: ICoverageMatrixRowResponse): string => {
  const req = r.role?.name || `${r.capability?.name || 'Requirement'} (capability)`
  return `${r.requiredCount}× ${req}${r.locationNode ? ` @ ${r.locationNode.name}` : ''}`
}

const phaseSub = (c: ICoverageMatrixCellResponse) =>
  c.phase === 1 ? 'confirmed roster' : c.phase === 2 ? 'attendance' : 'closed'

const STATUS_WORD: { [status: number]: string } = { 1: 'Full', 2: 'Under target', 3: 'Critical gap' }

export const CoverageMatrix = () => {
  const dispatch = useDispatch<AppDispatch>()
  const navigate = useNavigate()
  const { matrix, cell, isLoading, candidates, candidatesLoading } = useSelector(
    (state: RootState) => state.coverage,
  )
  const tree = useSelector((state: RootState) => state.locationRegistry.tree)
  const sites = useMemo(() => coverageSitesFromTree(tree), [tree])
  const [siteId, setSiteId] = useState<number | null>(null)
  const [date, setDate] = useState<Date>(new Date())
  const [coverOpen, setCoverOpen] = useState(false)
  const [moveWsId, setMoveWsId] = useState<number | null>(null)
  const [moveName, setMoveName] = useState('')
  const [moveTargetId, setMoveTargetId] = useState<number | null>(null)
  const [movePreview, setMovePreview] = useState<IReassignSourceImpactResponse | null>(null)

  const dateStr = date.toISOString().slice(0, 10)
  const todayStr = new Date().toISOString().slice(0, 10)
  const isToday = dateStr === todayStr
  const isPastDate = dateStr < todayStr

  useEffect(() => {
    fetchCoverageRegistries(dispatch)
  }, [dispatch])

  useEffect(() => {
    if (siteId == null && sites[0]) setSiteId(sites[0].id)
  }, [sites, siteId])

  useEffect(() => {
    if (siteId == null) return
    dispatch(getCoverageMatrix({ siteNodeId: siteId, date: dateStr }))
  }, [dispatch, siteId, dateStr])

  useEffect(() => {
    if (sites.length && siteId != null && !sites.some((s) => s.id === siteId)) setSiteId(sites[0].id)
  }, [sites, siteId])

  const cellFor = (deptIdx: number, shiftId: number): ICoverageMatrixCellResponse | undefined =>
    matrix?.departments[deptIdx]?.coverageMatrixCells.find((c) => c.shift.id === shiftId)

  const visibleShifts = useMemo(() => {
    if (!matrix) return []
    return matrix.shifts.filter((s) =>
      matrix.departments.some((d) =>
        d.coverageMatrixCells.some((c) => c.shift.id === s.id && c.isStaffed),
      ),
    )
  }, [matrix])

  const nowPct = useMemo(() => {
    const now = new Date()
    return pct(now.getHours() * 60 + now.getMinutes())
  }, [])

  const refetch = () => {
    if (siteId == null) return
    dispatch(getCoverageMatrix({ siteNodeId: siteId, date: dateStr }))
    if (cell) {
      dispatch(
        getCoverageCell({
          siteNodeId: siteId,
          departmentId: cell.department.id,
          shiftId: cell.shift.id,
          date: dateStr,
        }),
      )
    }
  }

  const resetMove = () => {
    setMoveWsId(null)
    setMoveName('')
    setMoveTargetId(null)
    setMovePreview(null)
  }

  const openCell = (departmentId: number, shiftId: number) => {
    if (siteId == null) return
    setCoverOpen(false)
    resetMove()
    dispatch(getCoverageCell({ siteNodeId: siteId, departmentId, shiftId, date: dateStr }))
  }

  const closeCell = () => {
    setCoverOpen(false)
    resetMove()
    dispatch(clearCoverageCell())
  }

  const startMove = (wsId: number, name: string) => {
    setCoverOpen(false)
    setMoveWsId(wsId)
    setMoveName(name)
    setMoveTargetId(null)
    setMovePreview(null)
  }

  const pickMoveTarget = (targetShiftId: number) => {
    if (moveWsId == null) return
    setMoveTargetId(targetShiftId)
    setMovePreview(null)
    dispatch(getReassignPreview({ workScheduleId: moveWsId, targetShiftId })).then((resp: any) => {
      if (resp.meta.requestStatus === 'fulfilled') setMovePreview(resp.payload)
    })
  }

  const doMove = (acknowledge: boolean) => {
    if (moveWsId == null || moveTargetId == null) return
    dispatch(
      applyCoverageReassign({
        workScheduleId: moveWsId,
        targetShiftId: moveTargetId,
        acknowledgeSourceImpact: acknowledge || undefined,
      }),
    ).then((resp: any) => {
      if (resp.meta.requestStatus === 'fulfilled') {
        resetMove()
        refetch()
      }
    })
  }

  const stripBars = (shift: ICoverageShiftResponse) => {
    const from = shift.shiftStart
    const to = shift.shiftStart + shift.minutes
    if (to > 1440) {
      return [
        { left: pct(from), width: pct(1440 - from) },
        { left: 0, width: pct(to - 1440) },
      ]
    }
    return [{ left: pct(from), width: pct(to - from) }]
  }

  const shiftActiveNow = (shift: ICoverageShiftResponse) => {
    if (!isToday) return false
    const now = new Date()
    const nowMin = now.getHours() * 60 + now.getMinutes()
    const from = shift.shiftStart
    const to = shift.shiftStart + shift.minutes
    return to > 1440 ? nowMin >= from || nowMin < to - 1440 : nowMin >= from && nowMin < to
  }

  const openCoverList = () => {
    if (!cell || siteId == null) return
    setCoverOpen(true)
    dispatch(getCoverCandidates({ siteNodeId: siteId, departmentId: cell.department.id, date: dateStr }))
  }

  const addCover = (userId: number) => {
    if (!cell || siteId == null) return
    dispatch(
      applyCoverageCover({
        userId,
        shiftId: cell.shift.id,
        date: dateStr,
        siteNodeId: siteId,
        departmentId: cell.department.id,
      }),
    ).then(() => {
      setCoverOpen(false)
      refetch()
    })
  }

  const detail = cell?.cell
  const posts = detail?.coverageMatrixRows.filter((r) => r.locationNode) || []
  const plainRows = detail?.coverageMatrixRows.filter((r) => !r.locationNode) || []
  const cellWindow = cell ? `${shiftWindowFrom(cell.shift)}–${shiftWindowTo(cell.shift)}` : ''
  const actionable = detail ? !detail.isFrozen && detail.phase !== 3 : false

  return (
    <>
      <NavBar />
      <PageWrapper noFooter>
        <Flex between center mb='16' style={{ minHeight: '2.5rem' }}>
          <Head1 style={{ fontSize: '1.5rem', lineHeight: 1.3 }} color={tokens.text.primary}>
            Coverage
          </Head1>
          <Button variant='secondary' onClick={() => navigate('/hr/schedule/coverage-models')}>
            Coverage Models ›
          </Button>
        </Flex>

        <HeaderControls>
          <CalendarInput
            nomarg
            width='220px'
            labelText='Date'
            defaultValue={date}
            onChange={(d: Date | null) => d && setDate(d)}
          />
          <HonestSelectInput
            width='200px'
            nomarg
            labelText='Site'
            placeholder='Select site…'
            options={sites.map((s) => ({ value: s.id, label: s.name }))}
            defaultValue={siteId || undefined}
            onChange={(v: ISelectValue) => setSiteId(Number(v.value))}
          />
        </HeaderControls>

        {isPastDate && <HistoricalBanner>Historical view — {dateStr} · cells are frozen snapshots</HistoricalBanner>}

        {isLoading && !matrix ? (
          <Flex justcenter mt='32'>
            <JustSpinner />
          </Flex>
        ) : !matrix || !matrix.departments.length ? (
          <EmptyState>
            <TitleText>No coverage models on {matrix?.site?.name || 'this site'} yet.</TitleText>
            <Button variant='primary' onClick={() => navigate('/hr/schedule/coverage-models')}>
              Set required staffing per department →
            </Button>
          </EmptyState>
        ) : (
          <>
            <StripWrap>
              <LabelsCol>
                {visibleShifts.map((s) => (
                  <LaneLabel key={s.id} $dim={!shiftActiveNow(s)}>
                    {s.name}
                  </LaneLabel>
                ))}
                <RulerSpacer />
              </LabelsCol>
              <TracksCol>
                {visibleShifts.map((s) => (
                  <LaneTrack key={s.id}>
                    {stripBars(s).map((b, i) => (
                      <StripBar key={i} $left={b.left} $width={b.width} $dim={!shiftActiveNow(s)}>
                        {b.width > 12 ? `${shiftWindowFrom(s)}–${shiftWindowTo(s)}` : ''}
                      </StripBar>
                    ))}
                  </LaneTrack>
                ))}
                <RulerTrack>
                  {[0, 6, 12, 18, 24].map((h) => (
                    <StripTickLabel key={h} $pct={pct(h * 60)} $edge={h === 0 ? 'start' : h === 24 ? 'end' : undefined}>
                      {String(h).padStart(2, '0')}:00
                    </StripTickLabel>
                  ))}
                </RulerTrack>
                {isToday && <NowLine $pct={nowPct} />}
              </TracksCol>
            </StripWrap>

            <MatrixScroll>
              <MatrixTable>
                <thead>
                  <tr>
                    <MatrixHeadCell>Department</MatrixHeadCell>
                    {visibleShifts.map((s) => (
                      <MatrixHeadCell key={s.id}>
                        {s.name} · {shiftWindowFrom(s)}–{shiftWindowTo(s)}
                      </MatrixHeadCell>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {matrix.departments.map((d, deptIdx) => {
                    const staffedCells = d.coverageMatrixCells.filter((c) => c.isStaffed)
                    const dayCovered = staffedCells.reduce((a, c) => a + c.coveredTotal, 0)
                    const dayRequired = staffedCells.reduce((a, c) => a + c.requiredTotal, 0)
                    return (
                      <tr key={d.department.id}>
                        <DeptCell>
                          <DeptCellName>{d.department.name}</DeptCellName>
                          <DeptCellSub>
                            {dayCovered}/{dayRequired} today
                          </DeptCellSub>
                        </DeptCell>
                        {visibleShifts.map((s) => {
                          const c = cellFor(deptIdx, s.id)
                          if (!c || !c.isStaffed) return <EmptyCellBox key={s.id}>—</EmptyCellBox>
                          const tone = coverageStatusTone(c.status)
                          return (
                            <Cell
                              key={s.id}
                              $phase={coveragePhaseKey(c.phase)}
                              $clickable
                              onClick={() => openCell(d.department.id, s.id)}
                            >
                              <CellTop>
                                <CellDot $tone={tone} />
                                <CellNumbers $tone={tone}>
                                  {c.coveredTotal}/{c.requiredTotal}
                                </CellNumbers>
                                {c.hasCriticalGap && <CritBadge>CRIT</CritBadge>}
                              </CellTop>
                              <CellSub>{phaseSub(c)}</CellSub>
                            </Cell>
                          )
                        })}
                      </tr>
                    )
                  })}
                </tbody>
              </MatrixTable>
            </MatrixScroll>

            <LegendRow>
              <span>
                <LegendSwatch $color={tokens.status.success.fg} /> covered
              </span>
              <span>
                <LegendSwatch $color={tokens.status.warning.fg} /> under target
              </span>
              <span>
                <LegendSwatch $color={tokens.status.danger.fg} /> critical gap
              </span>
              <span>
                <LegendDashed /> dashed = future → roster confirmations, not attendance
              </span>
            </LegendRow>
          </>
        )}
      </PageWrapper>

      {cell && detail && (
        <>
          <Scrim onClick={closeCell} />
          <Panel>
            <PanelHeader>
              <div>
                <TitleText weight='bold'>
                  {cell.department.name} — {cell.shift.name} ({cellWindow})
                </TitleText>
                <Flex gap='0.5rem' mt='8' center>
                  <StatusPill status={detail.phase === 2 ? 'info' : 'neutral'}>
                    {detail.phase === 1
                      ? 'Future · confirmed roster'
                      : detail.phase === 2
                        ? 'Active · attendance'
                        : detail.isFrozen
                          ? 'Past · frozen snapshot'
                          : 'Past · closed'}
                  </StatusPill>
                  <StatusPill status={coverageStatusTone(detail.status)} dot>
                    {detail.coveredTotal}/{detail.requiredTotal}
                  </StatusPill>
                </Flex>
                {detail.occurrenceDate !== dateStr && (
                  <OtherText grey>crew of {detail.occurrenceDate} (overnight shift)</OtherText>
                )}
              </div>
              <PanelClose type='button' aria-label='Close' onClick={closeCell}>
                <CloseIcon />
              </PanelClose>
            </PanelHeader>
            <PanelBody>
              {detail.hasCriticalGap && <GapCallout>Critical position unfilled — escalates at T−4h.</GapCallout>}

              {plainRows.length > 0 && (
                <div>
                  <SectionLabel>Roles</SectionLabel>
                  {plainRows.map((r, i) => (
                    <BreakdownRow key={i}>
                      <BreakdownLabel>{rowLabel(r)}</BreakdownLabel>
                      {r.isCritical && <CritBadge>CRITICAL</CritBadge>}
                      <BreakdownValue
                        $tone={coverageTone({
                          required: r.requiredCount,
                          covered: Math.min(r.actualCount, r.requiredCount),
                          criticalGap: r.isCritical && !r.isSatisfied,
                        })}
                      >
                        {detail.phase === 1
                          ? `${r.actualCount} / ${r.rosteredCount} / ${r.requiredCount}`
                          : `${Math.min(r.actualCount, r.requiredCount)}/${r.requiredCount}`}
                      </BreakdownValue>
                    </BreakdownRow>
                  ))}
                </div>
              )}

              {posts.length > 0 && (
                <div>
                  <SectionLabel>Posts</SectionLabel>
                  {posts.map((r, i) => (
                    <BreakdownRow key={i}>
                      <BreakdownLabel>{rowLabel(r)}</BreakdownLabel>
                      {r.isCritical && <CritBadge>CRITICAL</CritBadge>}
                      <BreakdownValue
                        $tone={coverageTone({
                          required: r.requiredCount,
                          covered: Math.min(r.actualCount, r.requiredCount),
                          criticalGap: r.isCritical && !r.isSatisfied,
                        })}
                      >
                        {Math.min(r.actualCount, r.requiredCount)}/{r.requiredCount}
                      </BreakdownValue>
                    </BreakdownRow>
                  ))}
                </div>
              )}

              <div>
                <SectionLabel>People</SectionLabel>
                {cell.people.map((p, i) =>
                  !p.user ? (
                    <VacantRow key={`v-${i}`}>
                      — vacant — {p.role?.name || p.capability?.name}
                      {p.locationNode ? ` @ ${p.locationNode.name}` : ''}
                    </VacantRow>
                  ) : (
                    <PersonRow key={p.workScheduleId || `p-${i}`}>
                      <PersonName>
                        {p.user.firstName} {p.user.lastName}
                      </PersonName>
                      <PersonRole>
                        {p.role?.name || p.capability?.name}
                        {p.locationNode ? ` @ ${p.locationNode.name}` : ''}
                        {p.state === 4 && p.lateByMinutes ? ` · ${p.lateByMinutes}m late` : ''}
                      </PersonRole>
                      <StatusPill status={STATE_PILL[p.state]?.tone || 'neutral'} dot>
                        {STATE_PILL[p.state]?.label || 'rostered'}
                      </StatusPill>
                      {actionable && detail.phase === 1 && p.workScheduleId != null && p.state === 1 && (
                        <Button
                          variant='secondary'
                          size='sm'
                          onClick={() => dispatch(confirmCoverageSlot(p.workScheduleId as number)).then(refetch)}
                        >
                          Confirm
                        </Button>
                      )}
                      {actionable && detail.phase === 1 && p.workScheduleId != null && p.state === 2 && (
                        <Button
                          variant='secondary'
                          size='sm'
                          onClick={() => dispatch(revokeCoverageConfirm(p.workScheduleId as number)).then(refetch)}
                        >
                          Revoke
                        </Button>
                      )}
                      {actionable && p.workScheduleId != null && (
                        <Button
                          variant='secondary'
                          size='sm'
                          onClick={() => startMove(p.workScheduleId as number, `${p.user?.firstName} ${p.user?.lastName}`)}
                        >
                          Move
                        </Button>
                      )}
                    </PersonRow>
                  ),
                )}
              </div>

              {moveWsId != null && (
                <div>
                  <SectionLabel>Move {moveName} to another shift</SectionLabel>
                  <HonestSelectInput
                    width='240px'
                    nomarg
                    placeholder='Target shift…'
                    options={(matrix?.shifts || [])
                      .filter((s) => s.id !== cell.shift.id)
                      .map((s) => ({ value: s.id, label: `${s.name} · ${shiftWindowFrom(s)}–${shiftWindowTo(s)}` }))}
                    onChange={(v: ISelectValue) => v?.value && pickMoveTarget(Number(v.value))}
                  />
                  {movePreview && (
                    <>
                      {movePreview.createsCriticalGapAtSource ? (
                        <GapCallout>
                          Blocked — moving {moveName} would leave a critical position short on this shift.
                        </GapCallout>
                      ) : (
                        <>
                          <OtherText grey>
                            This shift: {STATUS_WORD[movePreview.statusBefore]} → {STATUS_WORD[movePreview.statusAfter]}
                            {movePreview.degradesSource ? ' · the move makes this shift worse' : ''}
                          </OtherText>
                          <Flex gap='0.5rem' mt='8'>
                            <Button
                              variant={movePreview.degradesSource ? 'secondary' : 'primary'}
                              size='sm'
                              onClick={() => doMove(movePreview.degradesSource)}
                            >
                              {movePreview.degradesSource ? 'Move anyway' : 'Move'}
                            </Button>
                            <Button variant='secondary' size='sm' onClick={resetMove}>
                              Cancel
                            </Button>
                          </Flex>
                        </>
                      )}
                    </>
                  )}
                  {!movePreview && moveTargetId != null && (
                    <Flex justcenter mt='8'>
                      <JustSpinner />
                    </Flex>
                  )}
                </div>
              )}

              {coverOpen && (
                <div>
                  <SectionLabel>Cover candidates — free that day</SectionLabel>
                  {candidatesLoading ? (
                    <Flex justcenter mt='8'>
                      <JustSpinner />
                    </Flex>
                  ) : candidates.length ? (
                    candidates.map((c) => (
                      <PersonRow key={c.user.id}>
                        <PersonName>
                          {c.user.firstName} {c.user.lastName}
                        </PersonName>
                        <PersonRole>not rostered · additive</PersonRole>
                        <Button variant='primary' size='sm' onClick={() => addCover(c.user.id)}>
                          Add to shift
                        </Button>
                      </PersonRow>
                    ))
                  ) : (
                    <OtherText grey>No free department members on this site that day.</OtherText>
                  )}
                </div>
              )}
            </PanelBody>
            <PanelActions>
              {actionable ? (
                <>
                  <Button variant='primary' onClick={openCoverList}>
                    Request cover
                  </Button>
                  {detail.phase === 1 && (
                    <Button
                      variant='secondary'
                      onClick={() =>
                        siteId != null &&
                        dispatch(
                          sendCoverageReminder({
                            siteNodeId: siteId,
                            departmentId: cell.department.id,
                            shiftId: cell.shift.id,
                            date: dateStr,
                          }),
                        )
                      }
                    >
                      Send confirmation reminder
                    </Button>
                  )}
                </>
              ) : (
                <OtherText grey>
                  Closed cell — served from a frozen snapshot{detail.frozenAt ? `, ${detail.frozenAt.slice(0, 16).replace('T', ' ')}` : ''}. No actions available.
                </OtherText>
              )}
            </PanelActions>
          </Panel>
        </>
      )}
    </>
  )
}
